import Slider from "react-slick";
import {RefObject, useEffect, useRef, useState} from "react";

type ProductListBannerAutoplay = {
    containerRef: RefObject<HTMLDivElement>;
    shouldAutoplay: boolean;
};

export function useProductListBannerAutoplay(
    sliderRef: RefObject<Slider | null>,
    hasMultipleBanners: boolean
): ProductListBannerAutoplay {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isInViewport, setIsInViewport] = useState(false);
    const [isDocumentVisible, setIsDocumentVisible] = useState(!document.hidden);
    const shouldAutoplay = hasMultipleBanners && isInViewport && isDocumentVisible;

    useEffect(() => {
        const element = containerRef.current;

        if (!element) {
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            setIsInViewport(entry.isIntersecting);
        }, {threshold: 0.1});

        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        function onVisibilityChange(): void {
            setIsDocumentVisible(!document.hidden);
        }

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, []);

    useEffect(() => {
        if (shouldAutoplay) {
            sliderRef.current?.slickPlay();
        } else {
            sliderRef.current?.slickPause();
        }
    }, [shouldAutoplay]);

    return {containerRef, shouldAutoplay};
}
